import {ApolloLink, Observable} from '@apollo/client';
import logHttp from 'src/internal/logHttp';
import {getObservabilityMessenger} from 'src/internal/sailToolkitMessenger';
import loadId from 'src/internal/shared/loadId';
import {
  REQUEST_SOURCE_HEADER,
  getRequestSourceHeader,
  getRequestSourceInformation,
} from 'src/internal/shared/requestSource';
import {EXCLUDE_FROM_SENTRY} from 'src/internal/shared/telemetry/observability';
import {getOperationType} from 'src/internal/shared/utils/gql';

import type {
  GraphQlResponseError,
  InternalOperation,
} from 'src/internal/apollo/types';
import type {RequestSourceInformation} from 'src/internal/shared/requestSource';
import type {
  ErrorData,
  SailToolkitReport,
  SuccessDataAnalyticsReport,
  SuccessDataMetricsReport,
} from 'src/internal/telemetry/types';

type InternalOperationContext = ReturnType<InternalOperation['getContext']>;

declare global {
  interface Window {
    SAIL_CORE_DEV_TOOLS_PRESENT: boolean;
  }
}

const errorType = {
  expected: 'expected',
  muted: 'muted',
  unexpected: 'unexpected',
} as const;

type ErrorType = typeof errorType;

const networkErrorExtensions: GraphQlResponseError['extensions'] = {
  actionId: null,
  traceId: null,
  error: {
    code: 'network_error',
    source: 'network',
    status: -1,
  },
  unstable_requestId: null,
};

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

export function getOperationInformation(
  operation: InternalOperation,
): RequestSourceInformation {
  const context = operation.getContext();

  return getRequestSourceInformation({
    service: context.service,
    project: context.queryProject,
    component: context.component,
    operation: operation.operationName,
  });
}

function getType(
  context: InternalOperationContext,
  code: string,
): ErrorType[keyof ErrorType] {
  const {expectedErrors = [], mutedErrors = []} = context || {};

  if (expectedErrors.includes(code)) {
    return errorType.expected;
  }

  if (mutedErrors.includes(code)) {
    return errorType.muted;
  }

  return errorType.unexpected;
}

function getColor(context: InternalOperationContext, isRead: boolean): string {
  if (!isRead) {
    return 'primary-light';
  }
  if (context.isPrefetchMode && !context.isDeduplicated) {
    return 'secondary-light';
  }

  if (context.isDeduplicated) {
    return 'primary-light';
  }

  if (context.isPrefetchHit) {
    return 'tertiary';
  }

  return 'error';
}

function getTooltipText(
  context: InternalOperationContext,
  isRead: boolean,
): string {
  if (!isRead) {
    return 'Mutation hits the network';
  }

  if (context.isPrefetchMode && !context.isDeduplicated) {
    return `Issuing a network request for a prefetched query`;
  }

  if (context.isDeduplicated) {
    return `Not issuing a network request as there was already a request in flight`;
  }

  if (context.isPrefetchHit) {
    return `Retrieving result from prefetch cache (no network request)`;
  }

  return 'This query was not prefetched. Consider adding it to prefetch config.';
}

function shouldMarkPerformance(operationName: string): boolean {
  try {
    const ls = typeof window !== 'undefined' && window.localStorage;

    if (!ls) {
      return true;
    }

    const debugConfigItem = ls.getItem('DEBUG_SAIL_DATA');

    if (!debugConfigItem) {
      return true;
    }

    const debugConfigValue = JSON.parse(debugConfigItem);

    if (Array.isArray(debugConfigValue)) {
      return debugConfigValue.includes(operationName);
    }

    return true;
  } catch {
    return false;
  }
}

function markPerformance(
  operationId: string,
  operation: InternalOperation,
): void {
  if (typeof performance === 'undefined' || !performance.measure) {
    return;
  }

  if (!shouldMarkPerformance(operation.operationName)) {
    return;
  }

  const context = operation.getContext();
  const variables = operation.variables;
  const operationHash = context.operationHash;
  const operationName = operationHash
    ? `${operation.operationName} (${operationHash})`
    : operation.operationName;
  const isRead = getOperationType(operation.query) !== 'mutation';
  const isDeduplicated = !!context.isDeduplicated;
  const isPrefetchMode = !!context.isPrefetchMode;
  const isPrefetchHit = !!context.isPrefetchHit;
  const fetchPolicy = context.fetchPolicy;
  const color = getColor(context, isRead);

  try {
    performance.measure(operationName, {
      start: `${operationId}-start`,
      detail: {
        devtools: {
          dataType: 'track-entry',
          track: 'Apollo Links',
          trackGroup: '@sail/data',
          color,
          properties: [
            ['isPrefetchMode', isPrefetchMode],
            ['isPrefetchHit', isPrefetchHit],
            ['isDeduplicated', isDeduplicated],
            ['fetchPolicy', fetchPolicy],
            [
              'variables',
              JSON.stringify(variables, (_key, value) =>
                value === undefined ? '__UNDEFINED__' : value,
              ),
            ],
          ],
          tooltipText: getTooltipText(context, isRead),
        },
      },
    });
  } catch {
    // Ignore error, as 100% of them in the last week have come from very old browsers
    // that don't support the options object passed as second argument to this function.
    // See https://developer.mozilla.org/en-US/docs/Web/API/Performance/measure#browser_compatibility
    // for more details.
  }
}

function processSuccess(
  operation: InternalOperation,
  duration: number,
  operationId: string,
): void {
  markPerformance(operationId, operation);

  const context = operation.getContext();
  const {service, project, component} = getOperationInformation(operation);

  const tags: SuccessDataMetricsReport = {
    service,
    project,
    component,

    operation_name: operation.operationName,
    operation_tag: context.queryTag ?? '<unknown>',
    operation_deduplicate: !!context.isDeduplicated,
    operation_prefetch: !!context.isPrefetchMode,
    operation_prefetch_hit: !!context.isPrefetchHit,
  };

  context.observabilityFns?.metrics.increment('frontend.data.success', tags);

  const analyticsTags: SuccessDataAnalyticsReport = {
    // Ownership information:
    service,
    project,
    component,

    // Operation information:
    operation_name: operation.operationName,
    operation_tag: context.queryTag ?? '<unknown>',
    operation_deduplicate: !!context.isDeduplicated,
    operation_prefetch: !!context.isPrefetchMode,
    operation_prefetch_hit: !!context.isPrefetchHit,

    // Network information:
    network_duration: duration ?? null,
    network_load_id: loadId,
  };

  context.observabilityFns?.analytics.track(
    'frontend.data.success',
    analyticsTags,
  );

  const toolkitTags: SailToolkitReport = {
    service,
    project,
    component,
    operation_name: operation.operationName,
    operation_prefetch: !!context.isPrefetchMode,
    operation_prefetch_hit: !!context.isPrefetchHit,
    operation_deduplicate: !!context.isDeduplicated,
    network_duration: duration ?? null,
    variables: JSON.stringify(operation.variables),
  };

  sendEventDataToToolkit(toolkitTags, 'success');
}

function processError(
  operation: InternalOperation,
  duration: number,
  error: GraphQlResponseError,
  operationId: string,
): void {
  markPerformance(operationId, operation);

  const context = operation.getContext();
  const {service, project, component} = getOperationInformation(operation);

  if (context.observabilityFns) {
    const code = error.extensions?.error?.code || 'could_not_get_error_code';
    const type = getType(context, code);

    const tags: ErrorData = {
      // Ownership information:
      service,
      project,
      component,

      // Error information:
      errorCode: code,
      errorMessage: error.message || 'No message provided',
      errorPath: (error.path || []).join('.'),
      errorExpected: type === errorType.expected,
      errorMuted: type === errorType.muted,
      errorSource: error.extensions?.error?.source ?? '<unknown>',

      // Operation information:
      operationTag: context.queryTag ?? '<unknown>',
      operationName: operation.operationName,
      operationPrefetch: !!context.isPrefetchMode,
      operationPrefetchHit: !!context.isPrefetchHit,
      operationDeduplicate: !!context.isDeduplicated,

      // Network information:
      networkDuration: duration ?? null,
      networkStatus: error.extensions?.error?.status ?? null,
      networkActionId: error.extensions?.actionId ?? null,
      networkRequestId: error.extensions?.unstable_requestId ?? null,
      networkTraceId: error.extensions?.traceId ?? null,
    };

    logHttp('GraphQL', context.observabilityFns, tags);

    const toolkitTags: SailToolkitReport = {
      service,
      project,
      component,
      error_expected: tags.errorExpected,
      error_muted: tags.errorMuted,
      error_source: tags.errorSource,
      operation_name: tags.operationName,
      operation_prefetch: tags.operationPrefetch,
      operation_prefetch_hit: tags.operationPrefetchHit,
      operation_deduplicate: tags.operationDeduplicate,
      network_duration: tags.networkDuration,
      variables: JSON.stringify(operation.variables),
    };

    sendEventDataToToolkit(toolkitTags, 'error');
  }
}

function sendEventDataToToolkit(
  report: SailToolkitReport,
  type: 'error' | 'success',
): void {
  if (typeof window === 'undefined' || !window.SAIL_CORE_DEV_TOOLS_PRESENT) {
    return;
  }

  const messenger = getObservabilityMessenger();

  if (messenger) {
    messenger.sendMessage('sail_data_observability_event', {
      ...report,
      type,
      network_duration: report.network_duration ?? undefined,
    });
  }
}

/**
 * @see https://sail.stripe.me/data/api/createObservabilityLink
 */
export default function createObservabilityLink(): ApolloLink {
  let count = 0;

  return new ApolloLink(function observabilityLink(
    operation: InternalOperation,
    forward,
  ) {
    const tmpCount = count;
    count += 1;

    const context = operation.getContext();
    const {headers} = context;

    const info = getOperationInformation(operation);
    const xRequestSource = getRequestSourceHeader({
      ...info,
      ...(context.isPrefetchMode ? {is_prefetch: true} : {}),
    });
    const operationId = `${operation.operationName}-${tmpCount}`;

    operation.setContext({
      headers: {
        [REQUEST_SOURCE_HEADER]: xRequestSource,
        ...headers,
      },
    });

    return new Observable(function observabilityObserver(observer) {
      const start = now();

      typeof performance !== 'undefined' &&
        performance.mark &&
        performance.mark(`${operationId}-start`);

      const subscription = forward(operation).subscribe({
        next: function observabilityLinkNext(result) {
          const duration = Math.round(now() - start);

          if (result.errors) {
            result.errors.forEach((err: GraphQlResponseError) => {
              if (typeof err === 'object') {
                (err as any)[EXCLUDE_FROM_SENTRY] = true;
              }

              processError(operation, duration, err, operationId);
            });
          } else {
            processSuccess(operation, duration, operationId);
          }

          return observer.next(result);
        },

        error: function observabilityLinkError(_error: unknown) {
          const error = sanitizeError(_error);
          const duration = Math.round(now() - start);

          processError(
            operation,
            duration,
            {
              message: error.message,
              locations: [],
              path: ['_network_'],
              extensions: networkErrorExtensions,
            },
            operationId,
          );

          // Flag the error object as handled by us.
          error[EXCLUDE_FROM_SENTRY] = true;

          return observer.error(error);
        },

        complete: function observabilityLinkComplete() {
          return observer.complete();
        },
      });

      return function observabilityLinkUnsubscribe(): void {
        if (subscription) {
          subscription.unsubscribe();
        }
      };
    });
  });
}

function sanitizeError(error: any): Error & {[EXCLUDE_FROM_SENTRY]?: boolean} {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'string') {
    return new Error(error);
  }

  if (error && 'message' in error) {
    return new Error(String(error.message));
  }

  return new Error(`Unknown error caught by observability link. ${error}`);
}
